import { Component, OnInit } from '@angular/core';
import { BenefitType } from '@models/payroll/benefit-type.model';
import { CompanyBenefit } from '@models/payroll/company/benefit.model';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyBenefitForm } from '@forms/payroll/components';
import { AuthService, NotifyService } from '@app/services';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { NgForm } from '@angular/forms';
import { BenefitCategory } from '@models/benefits/benefit-category.model';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';
import { Benefit } from '@models/benefits/benefit.model';

type BenefitPayrollCategory = {
    payrollBenefitTypeId: number;
    hrBenefitName: string;
};

@Component({
    templateUrl: './payroll-benefit-store.template.html',
    styleUrls: ['./payroll-benefit-store.style.scss'],
})
export class PayrollBenefitStoreView implements OnInit {
    isLoading = true;
    benefit: CompanyBenefit = new CompanyBenefit();
    benefitType: BenefitType = new BenefitType();
    benefitSettingsRoute = ['/', 'benefits', 'settings'];

    benefitCategories!: BenefitPayrollCategory[];

    constructor(
        public readonly route: ActivatedRoute,
        private notify: NotifyService,
        private auth: AuthService,
        private router: Router,
        private http: HttpClient
    ) {}

    async ngOnInit(): Promise<void> {
        this.benefitCategories = await this.getFilteredBenefitCategories();
        this.isLoading = false;
    }

    async onBenefitTypeChanged(benefitTypeId: number): Promise<void> {
        this.benefitType = await BenefitType.with('t4_boxes').find(benefitTypeId);
    }

    async save(form: CompanyBenefitForm, benefitCategoryForm: NgForm): Promise<void> {
        form.submit();
        benefitCategoryForm.onSubmit(null);

        if (form.form.invalid || benefitCategoryForm.invalid) {
            return;
        }

        this.isLoading = true;

        try {
            const [, meta] = await CompanyBenefit.param('company', this.auth.company.id)
                .where('benefit_type_ids', this.benefit.benefitTypeId)
                .limit(1)
                .get();

            if (meta.page.totalSize > 0) {
                this.notify.error('benefits.benefitsPayroll.thisBenefitCategoryHasAlreadyBeenAddedToPayroll');
                this.isLoading = false;
                return;
            }

            const deductionFrequency = this.benefit.benefitDeductionFrequencyUuid;

            await this.benefit.param('company', this.auth.company.id).save();

            // This is a bit of a hack to prevent the form from appearing as invalid.
            // payroll isn't returning this value from the save endpoint, so when
            // angular deserializes a null value into it, and it appears as if it wasn't selected on the UI
            this.benefit.benefitDeductionFrequencyUuid = deductionFrequency;

            await this.syncEmployees(this.benefit.id);
            this.router.navigate(this.benefitSettingsRoute, { relativeTo: this.route });
            this.notify.success('saved');
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    private async syncEmployees(payrollBenefitId: number): Promise<any> {
        const endpoint = `${environment.api}/v2/benefitManagement/syncEmployeesToPayrollBenefit`;
        const payload = {
            payrollBenefitPlanId: payrollBenefitId,
        };

        return this.http.post(endpoint, payload).toPromise();
    }

    /**
     * Return an array of benefit categories for which have a mapping to a payroll benefit type and  there is a benefit
     * of that category within any plan in this company.
     *
     * This will be used to create a pseudo benefit category selector on the form, that will be used to choose the payroll
     * benefit type.
     */
    private async getFilteredBenefitCategories(): Promise<BenefitPayrollCategory[]> {
        const [[benefitCategories], [payrollBenefitTypes], [benefitPlans]] = await Promise.all([
            BenefitCategory.all(),
            BenefitType.get(),
            BenefitPlan.param('company', this.auth.company.id).with('benefits.benefitCategory').all(),
        ]);

        // This creates a new set from existing benefit categories that exist on benefits.
        const benefitCategoryCodeSet = new Set(
            benefitPlans.flatMap((benefitPlan) =>
                benefitPlan.benefits.map((benefit: Benefit) => benefit.benefitCategory.code)
            )
        );

        const categoryMap: BenefitPayrollCategory[] = [];

        benefitCategories.forEach(function (benefitCategory: BenefitCategory) {
            if (!benefitCategory.payrollBenefitTypeName) {
                return;
            }

            if (!benefitCategoryCodeSet.has(benefitCategory.code)) {
                return;
            }

            const mappedBenefit = payrollBenefitTypes.find(
                (payrollBenefitType) => payrollBenefitType.name === benefitCategory.payrollBenefitTypeName
            );

            if (mappedBenefit) {
                categoryMap.push({
                    payrollBenefitTypeId: mappedBenefit.id,
                    hrBenefitName: benefitCategory.name,
                });
            }
        });

        return categoryMap;
    }
}
